import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { EnrollRequest, EnrollResponse, GuestSessionResponse, OtpStartResponse, OtpVerifyResponse, SessionGrant } from '@ulp/consumer-core';
import { apiFetch, configureClient } from './api';
import { readJson, removeKey, writeJson } from './storage';

interface StoredSession {
  token: string;
  memberId: string;
  guest: boolean;
}

export interface VerifyInput {
  challengeId: string;
  code: string;
  acceptTerms?: boolean;
  marketingConsent?: boolean;
  termsVersion?: string;
}

export interface SessionValue {
  token: string | null;
  memberId: string | null;
  guest: boolean;
  signedIn: boolean;
  startCode(input: { email?: string; phone?: string }): Promise<OtpStartResponse>;
  verifyCode(input: VerifyInput): Promise<OtpVerifyResponse>;
  continueAsGuest(): Promise<void>;
  /** Phase 1: uygulamasız kayıt (ad + telefon + KVKK). Oturum dönerse saklanır; SMS adımı gerekiyorsa aynen döner. */
  enroll(input: EnrollRequest): Promise<EnrollResponse>;
  /** Hesap kurtarma / kasada bağlantı sonrası sunucunun verdiği oturumu benimse. */
  adopt(grant: SessionGrant): void;
  signOut(): void;
}

const Ctx = createContext<SessionValue | null>(null);

/**
 * Üye oturumu (işletme bazında). Token localStorage'da; iOS kurulmamış PWA'da veri 7 günde silinebilir → oturum
 * düşerse kullanıcı e-posta koduyla tekrar girer (hesap e-postaya bağlı, veri kaybı yok).
 */
export function SessionProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const qc = useQueryClient();
  const [session, setSession] = useState<StoredSession | null>(() => readJson<StoredSession>(slug, 'session'));

  // İstemci token'ı render sırasında senkron ayarlanır (ilk sorgular token'lı gitsin).
  configureClient({ token: session?.token ?? null });

  const persist = useCallback(
    (s: StoredSession | null) => {
      setSession(s);
      configureClient({ token: s?.token ?? null });
      if (s) writeJson(slug, 'session', s);
      else removeKey(slug, 'session');
    },
    [slug],
  );

  const signOut = useCallback(() => {
    persist(null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'bootstrap' });
  }, [persist, qc]);

  useEffect(() => {
    configureClient({ onUnauthorized: () => signOut() });
    return () => configureClient({ onUnauthorized: null });
  }, [signOut]);

  const startCode = useCallback(
    (input: { email?: string; phone?: string }) =>
      apiFetch<OtpStartResponse>('/app/auth/otp/start', { method: 'POST', body: input, anonymous: true }),
    [],
  );

  const verifyCode = useCallback(
    async (input: VerifyInput) => {
      const res = await apiFetch<OtpVerifyResponse>('/app/auth/otp/verify', {
        method: 'POST',
        anonymous: true,
        body: {
          ...input,
          // Misafirken giriş → misafir siparişleri üyeye taşınır.
          ...(session?.guest ? { guestMemberId: session.memberId } : {}),
        },
      });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'bootstrap' });
      persist({ token: res.token, memberId: res.memberId, guest: false });
      return res;
    },
    [persist, qc, session],
  );

  const continueAsGuest = useCallback(async () => {
    const res = await apiFetch<GuestSessionResponse>('/app/guest/session', { method: 'POST', body: { platform: 'web' }, anonymous: true });
    persist({ token: res.token, memberId: res.memberId, guest: true });
  }, [persist]);

  const enroll = useCallback(
    async (input: EnrollRequest) => {
      const res = await apiFetch<EnrollResponse>('/app/enroll', { method: 'POST', body: input, anonymous: true });
      if ('token' in res) {
        qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'bootstrap' });
        persist({ token: res.token, memberId: res.memberId, guest: false });
      }
      return res;
    },
    [persist, qc],
  );

  const adopt = useCallback(
    (grant: SessionGrant) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'bootstrap' });
      persist({ token: grant.token, memberId: grant.memberId, guest: false });
    },
    [persist, qc],
  );

  const value = useMemo<SessionValue>(
    () => ({
      token: session?.token ?? null,
      memberId: session?.memberId ?? null,
      guest: session?.guest ?? false,
      signedIn: !!session && !session.guest,
      startCode,
      verifyCode,
      continueAsGuest,
      enroll,
      adopt,
      signOut,
    }),
    [session, startCode, verifyCode, continueAsGuest, enroll, adopt, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession SessionProvider içinde kullanılmalı');
  return ctx;
}
